import { useState, useEffect } from 'react';
import { Rocket, Loader2, CalendarDays } from 'lucide-react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

// Renders **bold** markdown spans without dangerouslySetInnerHTML
function MdText({ text }) {
  const parts = String(text).split('**');
  return parts.map((p, i) => (i % 2 === 1 ? <strong key={i} className="font-semibold text-foreground">{p}</strong> : <span key={i}>{p}</span>));
}

// iter821 — Deployment Changelog: auto-lists what changed in each release
// (parsed server-side from the maintained CHANGELOG.md).
export function DeploymentChangelogView() {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get('/admin/deployment-changelog')
      .then(r => setReleases(r.data?.releases || []))
      .catch(() => setReleases([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  if (!releases.length) {
    return (
      <Card data-testid="changelog-empty"><CardContent className="py-12 text-center text-muted-foreground text-sm">
        No changelog entries found.
      </CardContent></Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="deployment-changelog-view">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Rocket size={15} className="text-orange-500" />
        <span>{releases.length} releases — newest first. Each entry lists what changed in that deployment.</span>
      </div>
      {releases.map((rel, idx) => {
        const isOpen = expanded === null ? idx < 3 : expanded === idx;
        return (
          <Card key={`${rel.release}-${idx}`} className="overflow-hidden" data-testid={`changelog-release-${rel.release}`}>
            <button
              className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-muted/50 transition-colors"
              onClick={() => setExpanded(expanded === idx ? -1 : idx)}
              data-testid={`changelog-toggle-${rel.release}`}
            >
              <Badge variant="outline" className="font-mono text-[11px] shrink-0 border-orange-300 text-orange-600">{rel.release}</Badge>
              <span className="text-sm font-medium flex-1 truncate">{rel.title || 'Release'}</span>
              {rel.date && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                  <CalendarDays size={12} /> {rel.date}
                </span>
              )}
              <span className="text-xs text-muted-foreground shrink-0">{rel.changes.length} changes</span>
            </button>
            {isOpen && rel.changes.length > 0 && (
              <CardContent className="pt-0 pb-4">
                <ul className="space-y-1.5 border-l-2 border-orange-200 dark:border-orange-900 pl-4 ml-1">
                  {rel.changes.map((c, i) => (
                    <li key={i} className="text-[13px] text-muted-foreground leading-relaxed">
                      <MdText text={c} />
                    </li>
                  ))}
                </ul>
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
}
